import type { ChatRealtimeEvent } from '@/features/chat/types';

type ChatRealtimeClientOptions = {
  websocketUrl: string;
  onEvent: (event: ChatRealtimeEvent) => void;
  onOpen?: () => void;
  onClose?: (event: CloseEvent) => void;
  onError?: (event: Event) => void;
};

export class ChatRealtimeClient {
  private socket: WebSocket | null = null;

  constructor(private readonly options: ChatRealtimeClientOptions) {}

  connect() {
    if (this.socket) {
      return;
    }

    const socket = new WebSocket(this.options.websocketUrl);
    this.socket = socket;

    socket.addEventListener('open', () => {
      this.options.onOpen?.();
    });

    socket.addEventListener('message', (event) => {
      try {
        this.options.onEvent(JSON.parse(event.data) as ChatRealtimeEvent);
      } catch {
        this.options.onEvent({
          type: 'chat.error',
          code: 'invalid_event_payload',
        });
      }
    });

    socket.addEventListener('close', (event) => {
      if (this.socket === socket) {
        this.socket = null;
      }

      this.options.onClose?.(event);
    });

    socket.addEventListener('error', (event) => {
      this.options.onError?.(event);
    });
  }

  subscribe(conversationId: string) {
    this.send({
      action: 'subscribe',
      conversation_id: conversationId,
    });
  }

  unsubscribe(conversationId: string) {
    this.send({
      action: 'unsubscribe',
      conversation_id: conversationId,
    });
  }

  disconnect() {
    this.socket?.close();
    this.socket = null;
  }

  private send(payload: Record<string, unknown>) {
    if (this.socket?.readyState !== WebSocket.OPEN) {
      return;
    }

    this.socket.send(JSON.stringify(payload));
  }
}
